import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { readCoverFile } from '@/lib/patientReports/coverFile';
import { uploadDocxTemplate } from '@/lib/patientReports/docxFile';
import { readDocxTemplateFile } from '@/lib/patientReports/docxTemplate';
import {
  clinicWordTemplatePath,
  letterheadObjectPath,
  parsePatientReportCatalog,
  reportTemplateCoverPath,
  reportTemplateWordPath,
  parsePatientReportList,
  parsePatientReportRow,
  parsePatientReportSettings,
  type PatientReportBlock,
  type PatientReportCatalog,
  type PatientReportLetterheadChoice,
  type PatientReportLetterheadMode,
  type PatientReportRow,
  type PatientReportSettings,
  type PatientReportSourceRef,
} from '@/lib/patientReports/model';
import {
  parsePatientReportTemplate,
  parsePatientReportTemplateList,
  savePatientReportRpcArgs,
  type PatientReportTemplate,
  type ReportSection,
} from '@/lib/patientReports/template';

type RpcError = { message: string };

async function reportRpc(name: string, args: Record<string, unknown>): Promise<unknown> {
  // Must stay a method call. Copying `supabase.rpc` into a local drops
  // `this`, and the client throws before the request is sent.
  const { data, error } = await (
    supabase as unknown as {
      rpc: (
        fn: string,
        params: Record<string, unknown>,
      ) => PromiseLike<{ data: unknown; error: RpcError | null }>;
    }
  ).rpc(name, args);
  if (error) throw new Error(error.message);
  return data;
}

export const patientReportSettingsKey = (clinicId: string | null | undefined) =>
  ['patient-reports', 'settings', clinicId ?? null] as const;

export const patientReportListKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['patient-reports', 'list', clinicId ?? null, patientId ?? null] as const;

export const patientReportSourcesKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['patient-reports', 'sources', clinicId ?? null, patientId ?? null] as const;

export const patientReportTemplatesKey = (clinicId: string | null | undefined) =>
  ['patient-reports', 'templates', clinicId ?? null] as const;

const EMPTY_SETTINGS: PatientReportSettings = {
  enabled: false,
  letterhead: 'none',
  letterheadStoragePath: null,
  letterheadMime: null,
  docxTemplateStoragePath: null,
};

export function usePatientReportSettings(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: patientReportSettingsKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<PatientReportSettings> => {
      if (!clinicId) return EMPTY_SETTINGS;
      const data = await reportRpc('get_clinic_patient_report_settings', { p_clinic_id: clinicId });
      return parsePatientReportSettings(data);
    },
  });
}

export function usePatientReportsEnabled(clinicId: string | null | undefined) {
  const query = usePatientReportSettings(clinicId);
  return { ...query, data: query.data?.enabled === true };
}

export function useSavePatientReportSettings(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      enabled: boolean;
      letterhead: PatientReportLetterheadMode;
      file?: File | null;
      existingPath?: string | null;
      existingMime?: string | null;
    }): Promise<PatientReportSettings> => {
      if (!clinicId) throw new Error('No active clinic');
      let path = input.existingPath ?? null;
      let mime = input.existingMime ?? null;
      if (input.letterhead === 'custom' && input.file) {
        mime = input.file.type === 'image/jpg' ? 'image/jpeg' : input.file.type;
        if (mime !== 'application/pdf' && mime !== 'image/png' && mime !== 'image/jpeg') {
          throw new Error('letterhead-type');
        }
        path = letterheadObjectPath(clinicId, input.file.name);
        const { error } = await supabase.storage.from('patient-reports').upload(path, input.file, {
          contentType: mime,
          upsert: false,
        });
        if (error) throw error;
      }
      if (input.letterhead !== 'custom') {
        path = input.existingPath ?? null;
        mime = input.existingMime ?? null;
      }
      const data = await reportRpc('save_clinic_patient_report_settings', {
        p_clinic_id: clinicId,
        p_enabled: input.enabled,
        p_letterhead: input.letterhead,
        p_letterhead_storage_path: path,
        p_letterhead_mime: mime,
      });
      return parsePatientReportSettings(data);
    },
    onSuccess: (settings) => {
      qc.setQueryData(patientReportSettingsKey(clinicId), settings);
    },
  });
}

export function usePatientReportTemplates(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: patientReportTemplatesKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return { templates: [], tags: [] };
      const data = await reportRpc('list_patient_report_templates', { p_clinic_id: clinicId });
      return parsePatientReportTemplateList(data);
    },
  });
}

export function useSavePatientReportTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string | null;
      name: string;
      reportTitle: string;
      description: string;
      includeCover: boolean;
      coverTitle: string;
      coverBody: string;
      letterhead: PatientReportLetterheadChoice;
      sections: ReportSection[];
    }): Promise<PatientReportTemplate> => {
      if (!clinicId) throw new Error('No active clinic');
      const data = await reportRpc('save_patient_report_template', {
        p_clinic_id: clinicId,
        p_template_id: input.templateId,
        p_name: input.name,
        p_report_title: input.reportTitle,
        p_description: input.description,
        p_include_cover: input.includeCover,
        p_cover_title: input.coverTitle,
        p_cover_body: input.coverBody,
        p_letterhead: input.letterhead,
        p_sections: input.sections,
      });
      const row = parsePatientReportTemplate(data);
      if (!row) throw new Error('save-empty');
      return row;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: patientReportTemplatesKey(clinicId) });
    },
  });
}

export function useSaveClinicWordTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file?: File | null;
      clear?: boolean;
    }): Promise<PatientReportSettings> => {
      if (!clinicId) throw new Error('No active clinic');
      const path = clinicWordTemplatePath(clinicId);
      if (!input.clear) {
        if (!input.file) throw new Error('docx-type');
        const bytes = await readDocxTemplateFile(input.file);
        await uploadDocxTemplate(path, bytes);
      }
      const data = await reportRpc('set_clinic_patient_report_docx', {
        p_clinic_id: clinicId,
        p_storage_path: input.clear ? null : path,
      });
      return parsePatientReportSettings(data);
    },
    onSuccess: (settings) => {
      qc.setQueryData(patientReportSettingsKey(clinicId), settings);
    },
  });
}

export function useSavePatientReportTemplateDocx(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      file?: File | null;
      clear?: boolean;
    }): Promise<string | null> => {
      if (!clinicId) throw new Error('No active clinic');
      const path = reportTemplateWordPath(clinicId, input.templateId);
      if (!input.clear) {
        if (!input.file) throw new Error('docx-type');
        const bytes = await readDocxTemplateFile(input.file);
        await uploadDocxTemplate(path, bytes);
      }
      const data = await reportRpc('set_patient_report_template_docx', {
        p_clinic_id: clinicId,
        p_template_id: input.templateId,
        p_storage_path: input.clear ? null : path,
      });
      const row = data && typeof data === 'object' ? (data as { docx_storage_path?: unknown }) : {};
      return typeof row.docx_storage_path === 'string' ? row.docx_storage_path : null;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: patientReportTemplatesKey(clinicId) });
    },
  });
}

export function useSavePatientReportTemplateCover(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      file?: File | null;
      clear?: boolean;
    }): Promise<{ path: string | null; mime: string | null }> => {
      if (!clinicId) throw new Error('No active clinic');
      if (input.clear) {
        const data = await reportRpc('set_patient_report_template_cover', {
          p_clinic_id: clinicId,
          p_template_id: input.templateId,
          p_storage_path: null,
          p_mime: null,
        });
        return coverResult(data);
      }
      if (!input.file) throw new Error('cover-type');
      const cover = await readCoverFile(input.file);
      const path = reportTemplateCoverPath(clinicId, input.templateId, cover.ext);
      const copy = new ArrayBuffer(cover.bytes.byteLength);
      new Uint8Array(copy).set(cover.bytes);
      const { error } = await supabase.storage
        .from('patient-reports')
        .upload(path, new Blob([copy], { type: cover.mime }), {
          contentType: cover.mime,
          upsert: true,
        });
      if (error) throw error;
      const data = await reportRpc('set_patient_report_template_cover', {
        p_clinic_id: clinicId,
        p_template_id: input.templateId,
        p_storage_path: path,
        p_mime: cover.mime,
      });
      return coverResult(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: patientReportTemplatesKey(clinicId) });
    },
  });
}

function coverResult(data: unknown): { path: string | null; mime: string | null } {
  const row =
    data && typeof data === 'object'
      ? (data as { cover_storage_path?: unknown; cover_mime?: unknown })
      : {};
  return {
    path: typeof row.cover_storage_path === 'string' ? row.cover_storage_path : null,
    mime: typeof row.cover_mime === 'string' ? row.cover_mime : null,
  };
}

export function useDeletePatientReportTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      if (!clinicId) throw new Error('No active clinic');
      await reportRpc('delete_patient_report_template', {
        p_clinic_id: clinicId,
        p_template_id: templateId,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: patientReportTemplatesKey(clinicId) });
    },
  });
}

export function usePatientReportSources(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: patientReportSourcesKey(clinicId, patientId),
    enabled: Boolean(clinicId && patientId && enabled),
    queryFn: async (): Promise<PatientReportCatalog> => {
      const data = await reportRpc('list_patient_report_sources', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
      });
      return parsePatientReportCatalog(data);
    },
  });
}

export function usePatientReportList(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: patientReportListKey(clinicId, patientId),
    enabled: Boolean(clinicId && patientId && enabled),
    queryFn: async (): Promise<PatientReportRow[]> => {
      const data = await reportRpc('list_patient_reports', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
      });
      return parsePatientReportList(data);
    },
  });
}

export function useSavePatientReport(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reportId: string | null;
      title: string;
      blocks: PatientReportBlock[];
      sources: PatientReportSourceRef[];
      templateId?: string | null;
      letterhead?: PatientReportLetterheadChoice;
      includeCover?: boolean;
      coverTitle?: string;
      coverBody?: string;
      composition?: ReportSection[];
      /** Same list as composition. Kept so a sections payload is not stored empty. */
      sections?: ReportSection[];
    }): Promise<PatientReportRow> => {
      if (!clinicId || !patientId) throw new Error('Missing patient');
      const data = await reportRpc(
        'save_patient_report',
        savePatientReportRpcArgs({
          clinicId,
          patientId,
          ...input,
        }),
      );
      const row = parsePatientReportRow(data);
      if (!row) throw new Error('save-empty');
      return row;
    },
    onSuccess: async (row) => {
      const key = patientReportListKey(clinicId, patientId);
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<PatientReportRow[]>(key, (current) => [
        row,
        ...(current ?? []).filter((item) => item.id !== row.id),
      ]);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useFinishPatientReport(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reportId: string; pdf: Uint8Array }): Promise<PatientReportRow> => {
      if (!clinicId || !patientId) throw new Error('Missing patient');
      const path = `${clinicId}/${patientId}/${input.reportId}.pdf`;
      const pdfBytes = new ArrayBuffer(input.pdf.byteLength);
      new Uint8Array(pdfBytes).set(input.pdf);
      const { error } = await supabase.storage
        .from('patient-reports')
        .upload(path, new Blob([pdfBytes], { type: 'application/pdf' }), {
          contentType: 'application/pdf',
          upsert: true,
        });
      if (error) throw error;
      const data = await reportRpc('finish_patient_report', {
        p_clinic_id: clinicId,
        p_report_id: input.reportId,
        p_pdf_storage_path: path,
      });
      const row = parsePatientReportRow(data);
      if (!row) throw new Error('finish-empty');
      return row;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: patientReportListKey(clinicId, patientId) });
    },
  });
}

export function useDeletePatientReport(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reportId: string) => {
      if (!clinicId) throw new Error('No active clinic');
      await reportRpc('delete_patient_report', {
        p_clinic_id: clinicId,
        p_report_id: reportId,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: patientReportListKey(clinicId, patientId) });
    },
  });
}

export async function searchPreviewPatients(
  clinicId: string,
  query: string,
): Promise<{ id: string; name: string }[]> {
  const needle = query.replace(/[%_,]/g, '').trim();
  let request = supabase
    .from('patients')
    .select('id, first_name, last_name')
    .eq('clinic_id', clinicId)
    .neq('status', 'related')
    .order('last_name', { ascending: true })
    .limit(8);
  if (needle) {
    request = request.or(`first_name.ilike.%${needle}%,last_name.ilike.%${needle}%`);
  }
  const { data, error } = await request;
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    name: `${row.first_name} ${row.last_name}`.trim(),
  }));
}

export async function loadPatientReportPdfBlob(path: string): Promise<Blob> {
  const { data, error } = await supabase.storage.from('patient-reports').download(path);
  if (error || !data) throw error ?? new Error('download');
  return data;
}

export async function downloadPatientReportPdf(path: string, filename: string) {
  const { data, error } = await supabase.storage.from('patient-reports').download(path);
  if (error || !data) throw error ?? new Error('download');
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}
